/**
 * Razões de proporção da mídia social — fonte única de verdade, espelhando o
 * `SocialMediaRatios` do app mobile para manter o enquadramento consistente
 * entre mobile, desktop e web mobile.
 *
 * Valores no formato `largura / altura` (compatível com a CSS `aspect-ratio`).
 * Imagens são orientadas (paisagem/retrato); vídeo usa uma razão fixa por
 * contexto (igual ao mobile). A mídia é exibida com `object-fit: contain`
 * (mantém a imagem inteira dentro do quadro, sem cortar).
 */
export const SOCIAL_MEDIA_RATIOS = {
  feed: {
    landscapeImage: '16 / 9',
    portraitImage: '4 / 5',
    video: '4 / 5',
  },
  postDetails: {
    landscapeImage: '16 / 9',
    portraitImage: '4 / 5',
    video: '9 / 16',
  },
  explore: '1 / 1',
} as const;

export type SocialMediaContext = 'feed' | 'detail';

/**
 * Limites de enquadramento para IMAGEM (estilo Instagram): o quadro segue a
 * proporção REAL da imagem, apenas "limitada" entre um retrato máximo (4/5) e
 * uma paisagem máxima (1.91/1). Isso evita cards altos/largos demais e, como o
 * quadro acompanha a imagem, junto com `object-fit: cover` não sobra borda
 * (imagens dentro da faixa não são cortadas; só as fora da faixa recortam o excedente).
 */
const IMAGE_MIN_RATIO = 4 / 5;   // 0.8  — retrato mais "alto" permitido
const IMAGE_MAX_RATIO = 1.91;    // paisagem mais "larga" permitida

/**
 * Resolve a `aspect-ratio` (CSS) do quadro da mídia.
 * - Vídeo: razão fixa por contexto (inalterado).
 * - Imagem: proporção real (largura/altura) limitada à faixa [4/5 .. 1.91/1].
 *
 * @param naturalW largura natural da mídia
 * @param naturalH altura natural da mídia
 * @param isVideo  true se for vídeo (usa razão fixa por contexto)
 * @param context  'feed' (default) ou 'detail' (página do post)
 */
export function resolveMediaAspectRatio(
  naturalW: number,
  naturalH: number,
  isVideo: boolean,
  context: SocialMediaContext = 'feed'
): string {
  const set = context === 'detail' ? SOCIAL_MEDIA_RATIOS.postDetails : SOCIAL_MEDIA_RATIOS.feed;
  if (isVideo) {
    return set.video;
  }
  if (!naturalW || !naturalH) {
    return set.portraitImage; // fallback seguro (load falhou / sem dimensão) — evita card gigante
  }
  // Segue a proporção real, apenas limitada à faixa permitida.
  const ratio = naturalW / naturalH;
  const clamped = Math.min(IMAGE_MAX_RATIO, Math.max(IMAGE_MIN_RATIO, ratio));
  return `${clamped.toFixed(4)} / 1`;
}
